/**
 * The scaffold every Learn More page fills.
 *
 * A page module supplies the words, the chart and the builder arithmetic;
 * this puts them in the same order, in the same grid, with the same controls,
 * so the six pages read as siblings.
 */
import { MARKERS, markerValueFor } from '../../model/markers.js';
import { SPEC_BY_ID } from '../../model/config.js';
import { formatInput } from '../../format.js';
import { LEARN_ENTRIES } from '../../learn/registry.js';
import type { KeyEntry, LearnPageSpec, ProseBlock, Source } from '../../learn/types.js';
import {
  decodeScenario, defaultScenario, hashFor, learnHref, learnIndexHref,
  type Scenario,
} from '../../state.js';
import { plotTable, renderPlot, renderStrip, stripTable } from '../plot.js';
import { attachFigureButtons, type FigureButtons } from '../figure.js';
import { installThemeToggle } from '../theme.js';
import { linkToolbar } from '../toolbar.js';
import { collectOutputs, panel, type RenderReport } from '../report.js';
import { buildIdentity } from './identity.js';
import { renderBuilder, type BuilderHandle } from './builder.js';

const CREDIT = 'Analysis by Roger Pielke Jr., The Honest Broker';
/** Where the seven marker values come from, on every page that shows them. */
const MARKER_SOURCE = 'ScenarioMIP CMIP7 marker scenarios, as carried in this tool';

/**
 * The mark, the source of the numbers and the analysis credit, under every
 * figure and every table. A figure that leaves the page as an image has to
 * carry the same three things, which is why figureToPng draws them too.
 */
function buildCredit(root: Document, source: string): HTMLElement {
  const block = root.createElement('div');
  block.className = 'figure-credit';

  const logo = root.createElement('img');
  logo.src = '/thb-logo.png';
  logo.alt = 'The Honest Broker';
  logo.width = 216;
  logo.height = 216;
  block.appendChild(logo);

  const lines = root.createElement('div');
  const data = element(root, 'span', 'credit-data', `Data: ${source}`);
  const credit = element(root, 'span', 'credit', CREDIT);
  lines.append(data, credit);
  block.appendChild(lines);
  return block;
}

const OUTPUT_IDS = [
  'learn-title', 'learn-standfirst', 'definition-body', 'chart-caption',
  'markers-table', 'builder-result-value', 'sources-list',
];

function element<K extends keyof HTMLElementTagNameMap>(
  root: Document, tag: K, className?: string, text?: string,
): HTMLElementTagNameMap[K] {
  const node = root.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** One section of the page, in the same two-column band as the header. */
function band(root: Document, heading: string, note?: string): {
  section: HTMLElement; body: HTMLElement;
} {
  const section = element(root, 'section', 'learn-block banded');
  const left = element(root, 'div', 'band-left');
  const title = element(root, 'h2', undefined, heading);
  left.appendChild(title);
  if (note !== undefined) left.appendChild(element(root, 'p', 'block-note', note));
  const body = element(root, 'div', 'band-right');
  section.append(left, body);
  return { section, body };
}

function paragraphs(root: Document, into: HTMLElement, lines: readonly string[]): void {
  for (const line of lines) {
    const p = element(root, 'p');
    p.innerHTML = line;
    into.appendChild(p);
  }
}

function proseBlock(root: Document, block: ProseBlock): HTMLElement {
  const { section, body } = band(root, block.heading, block.note);
  paragraphs(root, body, block.paragraphs);
  return section;
}

const NUMBER_WORDS: Readonly<Record<number, string>> = Object.freeze({
  2: 'two', 3: 'three', 4: 'four', 5: 'five', 6: 'six', 7: 'seven', 8: 'eight', 9: 'nine',
});

/**
 * The seven markers' value for this page's factor.
 *
 * Two pages have no published value for any marker. Rather than seven dashes,
 * those show what this tool fits from each marker's CO2 path, under a column
 * headed "Derived" and a caption saying where the figures come from.
 */
function markerTable(root: Document, spec: LearnPageSpec): HTMLTableElement {
  const table = element(root, 'table', 'kaya markers-table');
  table.id = 'markers-table';
  const input = SPEC_BY_ID[spec.input];
  const published = MARKERS.map((marker) => markerValueFor(marker, spec.input));
  const derived = published.every((value) => value === null)
    && spec.derivedMarkerValue !== undefined;
  const values = derived
    ? MARKERS.map((marker) => spec.derivedMarkerValue?.(marker.id) ?? null)
    : published;
  const rows = MARKERS.map((marker, index) => {
    const value = values[index] ?? null;
    return `<tr><th scope="row"><span class="swatch" style="background:${marker.color}"></span>`
      + `CMIP7 ${marker.label}</th>`
      + `<td>${value === null ? '—' : formatInput(spec.input, value)}</td>`
      + `<td class="units">${input.units}</td></tr>`;
  }).join('');
  const caption = derived
    ? '<caption>No marker publishes a value for this control. These figures come from '
      + 'fitting each marker’s published CO₂ path, and a dash marks a marker this tool '
      + 'carries no preset for.</caption>'
    : published.every((value) => value === null)
      ? '<caption>No marker publishes a value for this control.</caption>'
      : '';
  table.innerHTML = caption
    + '<thead><tr><th scope="col">Scenario</th>'
    + `<th scope="col">${derived ? 'Derived' : 'Assumes'}</th>`
    + '<th scope="col">Units</th></tr></thead>'
    + `<tbody>${rows}</tbody>`;
  return table;
}

/** The key under a figure, in the same shape as the top page's legend. */
function buildKey(root: Document, entries: readonly KeyEntry[]): HTMLElement {
  const key = element(root, 'div', 'legend chart-key');
  for (const item of entries) {
    const entry = element(root, 'div', 'legend-item');
    const swatch = element(root, 'span', item.dot === true ? 'swatch swatch-dot'
      : (item.dash === true ? 'swatch swatch-dash' : 'swatch'));
    swatch.style.background = item.color;
    entry.appendChild(swatch);
    entry.appendChild(element(root, 'b', undefined, item.label));
    key.appendChild(entry);
  }
  return key;
}

function sourceList(root: Document, sources: readonly Source[]): HTMLElement {
  const list = element(root, 'ol', 'refs');
  list.id = 'sources-list';
  for (const source of sources) {
    const item = root.createElement('li');
    const line = element(root, 'p', 'ref');
    const link = root.createElement('a');
    link.href = source.url;
    link.textContent = source.title;
    link.rel = 'noopener';
    line.appendChild(link);
    line.appendChild(root.createTextNode(`. ${source.publisher}, ${source.vintage}.`));
    item.appendChild(line);
    item.appendChild(element(root, 'p', 'gloss', source.used));
    list.appendChild(item);
  }
  return list;
}

function siblingNav(root: Document, slug: string, scenario: Scenario): HTMLElement {
  const nav = element(root, 'nav', 'siblings banded');
  nav.setAttribute('aria-label', 'The other assumptions');
  const left = element(root, 'div', 'band-left');
  // Derived, because this heading said "five" from the day six pages existed
  // and stayed at five when timing and removal took the count to eight.
  const others = LEARN_ENTRIES.filter((entry) => entry.slug !== slug).length;
  left.appendChild(element(root, 'h2', undefined, `The other ${NUMBER_WORDS[others] ?? others}`));
  const body = element(root, 'div', 'band-right');
  const list = element(root, 'ul', 'sibling-list');
  for (const entry of LEARN_ENTRIES) {
    if (entry.slug === slug) continue;
    const item = root.createElement('li');
    if (entry.status === 'live') {
      const link = root.createElement('a');
      // learnHref, never a bare path: a bare one drops the reader's six
      // numbers and their name, and the next Use button then carries the
      // defaults back to the builder over everything they had set.
      link.href = learnHref(entry.slug, scenario);
      link.textContent = entry.title;
      item.appendChild(link);
    } else {
      item.appendChild(element(root, 'span', 'forthcoming', entry.title));
      item.appendChild(element(root, 'span', 'forthcoming-tag', 'in preparation'));
    }
    item.appendChild(element(root, 'span', 'sibling-summary', entry.summary));
    list.appendChild(item);
  }
  body.appendChild(list);
  nav.append(left, body);
  return nav;
}

/**
 * The strip of sibling pages at the head of every Learn More page.
 *
 * The foot of the page already lists the others with a line of description
 * each, which serves a reader who has finished. This serves one who has not:
 * it sits above the fold so moving between factors never means scrolling to
 * the bottom first, and it names the page you are on so the set reads as a set.
 *
 * Every link goes through learnHref, never a bare path. A bare one drops the
 * reader's eight numbers and their name, and the next Use button then carries
 * the defaults back to the builder over everything they had set.
 */
function pageBanner(root: Document, slug: string, scenario: Scenario): HTMLElement {
  const nav = element(root, 'nav', 'learn-banner');
  nav.setAttribute('aria-label', 'The Learn More pages');
  const list = element(root, 'div', 'learn-banner-list');
  for (const entry of LEARN_ENTRIES) {
    if (entry.slug === slug) {
      const here = element(root, 'span', 'learn-chip learn-chip-here', entry.title);
      here.setAttribute('aria-current', 'page');
      list.appendChild(here);
    } else if (entry.status === 'live') {
      const link = root.createElement('a');
      link.className = 'learn-chip';
      link.href = learnHref(entry.slug, scenario);
      link.textContent = entry.title;
      list.appendChild(link);
    } else {
      const soon = element(root, 'span', 'learn-chip learn-chip-soon', entry.title);
      soon.title = 'In preparation';
      list.appendChild(soon);
    }
  }
  const all = root.createElement('a');
  all.className = 'learn-chip learn-chip-index';
  all.href = learnIndexHref(scenario);
  all.textContent = 'All pages';
  list.appendChild(all);
  nav.appendChild(list);
  return nav;
}

function backLink(root: Document, scenario: Scenario, fresh: boolean): HTMLElement {
  const nav = element(root, 'nav', 'back-strip');
  const link = root.createElement('a');
  link.className = 'back-link';
  link.href = `/${hashFor(scenario)}`;
  link.textContent = fresh
    ? '← Start a fresh scenario'
    : '← Back to my scenario, unchanged';
  nav.appendChild(link);
  return nav;
}

export interface LearnPage {
  scenario: Scenario;
  builder: BuilderHandle;
  chart: SVGSVGElement;
  render(): RenderReport;
  lastReport(): RenderReport | null;
}

export function mountLearnPage(spec: LearnPageSpec, root: Document = document): LearnPage {
  const view = root.defaultView;
  const incoming = decodeScenario(view?.location.search ?? '');
  const scenario = incoming ?? defaultScenario();
  const fresh = incoming === null;

  const main = root.getElementById('learn-main');
  if (main === null) throw new Error('missing element #learn-main');
  main.textContent = '';

  // The page's own colour, and the reader's own name for their scenario.
  root.documentElement.style.setProperty('--accent', spec.accent);
  root.documentElement.dataset['learnPage'] = spec.slug;

  const title = root.getElementById('learn-title');
  if (title !== null) title.textContent = spec.title;
  const standfirst = root.getElementById('learn-standfirst');
  if (standfirst !== null) standfirst.textContent = spec.standfirst;

  linkToolbar(root, scenario);

  main.appendChild(pageBanner(root, spec.slug, scenario));
  main.appendChild(backLink(root, scenario, fresh));

  // The builder comes first: a reader arrives here to set a number, and the
  // explanation reads better once they have moved something.
  const builderBlock = band(root, spec.builder.heading, spec.builder.note);
  paragraphs(root, builderBlock.body, spec.builder.paragraphs);
  const builderHost = element(root, 'div', 'builder');
  builderBlock.body.appendChild(builderHost);
  builderBlock.section.classList.add('builder-block');
  main.appendChild(builderBlock.section);

  // Definition.
  const definition = band(root, 'What it measures');
  const facts = element(root, 'dl', 'definition-grid');
  const entries: Array<[string, string]> = [
    ['Quantity', spec.definition.quantity],
    ['Units', spec.definition.units],
    ['In the identity', spec.definition.place],
    ['Today', spec.definition.today],
  ];
  for (const [term, value] of entries) {
    const pair = element(root, 'div');
    pair.appendChild(element(root, 'dt', undefined, term));
    pair.appendChild(element(root, 'dd', undefined, value));
    facts.appendChild(pair);
  }
  definition.body.appendChild(facts);
  const body = element(root, 'div', 'definition-body');
  body.id = 'definition-body';
  paragraphs(root, body, spec.definition.paragraphs);
  definition.body.appendChild(body);
  main.appendChild(definition.section);

  // The identity, with this factor inked.
  const identity = band(root, 'Where it sits');
  identity.body.appendChild(buildIdentity(root, { active: spec.input, scenario }));
  main.appendChild(identity.section);

  // The record.
  const record = band(root, spec.chart.heading, spec.chart.note);
  paragraphs(root, record.body, spec.chart.paragraphs);
  const figure = element(root, 'figure', 'chart-figure');
  const chart = root.createElementNS('http://www.w3.org/2000/svg', 'svg');
  chart.id = 'learn-chart';
  chart.setAttribute('role', 'img');
  chart.setAttribute('aria-label', spec.chart.caption);
  figure.appendChild(chart);
  const caption = element(root, 'figcaption', 'caption');
  const captionText = element(root, 'span', undefined, spec.chart.caption);
  captionText.id = 'chart-caption';
  caption.appendChild(captionText);
  figure.appendChild(caption);
  figure.appendChild(buildCredit(root, spec.chart.dataSource));
  record.body.appendChild(figure);

  record.body.appendChild(buildKey(root, spec.chart.key));
  const chartButtons = attachFigureButtons(
    root, chart,
    { title: spec.title, source: spec.chart.dataSource, columns: [] },
    `${spec.slug}-figure`,
  );
  record.body.appendChild(chartButtons.element);

  // A second figure, where one chart cannot carry the story: a distribution
  // on one page, a second time series on another.
  let extra: SVGSVGElement | null = null;
  let extraButtons: FigureButtons | null = null;
  if (spec.chart.extra !== undefined) {
    const block = spec.chart.extra;
    const figure = element(root, 'figure', 'chart-figure extra-figure');
    extra = root.createElementNS('http://www.w3.org/2000/svg', 'svg');
    extra.id = 'learn-extra';
    extra.setAttribute('role', 'img');
    extra.setAttribute('aria-label', block.caption);
    figure.appendChild(extra);
    const extraCaption = element(root, 'figcaption', 'caption');
    const extraText = element(root, 'span', undefined, block.caption);
    extraText.id = 'extra-caption';
    extraCaption.appendChild(extraText);
    figure.appendChild(extraCaption);
    figure.appendChild(buildCredit(root, block.dataSource));
    record.body.appendChild(figure);
    if (block.key !== undefined) record.body.appendChild(buildKey(root, block.key));
    extraButtons = attachFigureButtons(
      root, extra, { title: spec.title, source: block.dataSource, columns: [] },
      `${spec.slug}-figure-2`,
    );
    record.body.appendChild(extraButtons.element);
  }
  main.appendChild(record.section);

  main.appendChild(proseBlock(root, spec.drivers));

  const markers = band(root, spec.markers.heading, spec.markers.note);
  paragraphs(root, markers.body, spec.markers.paragraphs);
  const scroller = element(root, 'div', 'scroll-x');
  scroller.appendChild(markerTable(root, spec));
  markers.body.appendChild(scroller);
  markers.body.appendChild(buildCredit(root, MARKER_SOURCE));
  main.appendChild(markers.section);

  const sources = band(root, 'Sources');
  sources.body.appendChild(sourceList(root, spec.sources));
  main.appendChild(sources.section);

  main.appendChild(siblingNav(root, spec.slug, scenario));
  main.appendChild(backLink(root, scenario, fresh));

  let report: RenderReport | null = null;
  let builder: BuilderHandle | null = null;

  function render(): RenderReport {
    const results: RenderReport['panels'] = [];
    const outcome = builder?.outcome();
    if (outcome === undefined) return { panels: results, outputs: {} };
    panel(results, 'chart', chart, () => {
      const plot = spec.chart.spec(outcome, scenario);
      renderPlot(chart, plot);
      chartButtons.update(
        plotTable(plot, `${spec.title} — ${spec.chart.heading}`, spec.chart.dataSource),
      );
    });
    if (extra !== null && spec.chart.extra !== undefined) {
      const block = spec.chart.extra;
      panel(results, 'extra', extra, () => {
        if (extra === null) return;
        if (block.kind === 'strip') {
          const strip = block.spec(outcome, scenario);
          renderStrip(extra, strip);
          extraButtons?.update(
            stripTable(strip, `${spec.title} — distribution`, block.dataSource),
          );
        } else {
          const plot = block.spec(outcome, scenario);
          renderPlot(extra, plot);
          extraButtons?.update(
            plotTable(plot, `${spec.title} — second figure`, block.dataSource),
          );
        }
      });
    }
    report = { panels: results, outputs: collectOutputs(root, OUTPUT_IDS) };
    return report;
  }

  builder = renderBuilder(root, builderHost, spec.builder, spec.input, scenario, () => {
    render();
  });
  const resultValue = builderHost.querySelector('.builder-result-value');
  if (resultValue !== null) resultValue.id = 'builder-result-value';

  const themeButton = root.getElementById('theme-toggle');
  const themeLabel = root.getElementById('theme-label');
  if (themeButton !== null && themeLabel !== null) installThemeToggle(themeButton, themeLabel);

  render();
  // The charts size their margins to the labels as printed. Drawn before the
  // web font arrives, they measured the fallback face, so draw again once the
  // real one is in.
  void root.fonts?.ready.then(() => { render(); });
  return { scenario, builder, chart, render, lastReport: () => report };
}
